// NEW_FILE_JUSTIFIED: Text entry form state for KnowledgeBaseTextsTab — single state object to stay under 2-useState-per-component limit
import { useState, useCallback } from 'react';
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: hooks in src/hooks/ need convex Id type

interface ITextEntryFormState {
  title: string;
  content: string;
  isShared: boolean;
  isSaving: boolean;
  selectedIds: Set<Id<'knowledgeBaseDocuments'>>;
}

const INITIAL_STATE: ITextEntryFormState = {
  title: '',
  content: '',
  isShared: false,
  isSaving: false,
  selectedIds: new Set(),
};

export function useTextEntryForm() {
  const [state, setState] = useState<ITextEntryFormState>(INITIAL_STATE);

  const setTitle = useCallback((title: string) => {
    setState(prev => ({ ...prev, title }));
  }, []);

  const setContent = useCallback((content: string) => {
    setState(prev => ({ ...prev, content }));
  }, []);

  const setIsShared = useCallback((isShared: boolean) => {
    setState(prev => ({ ...prev, isShared }));
  }, []);

  const setIsSaving = useCallback((isSaving: boolean) => {
    setState(prev => ({ ...prev, isSaving }));
  }, []);

  const toggleSelect = useCallback((id: Id<'knowledgeBaseDocuments'>) => {
    setState(prev => {
      const next = new Set(prev.selectedIds);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return { ...prev, selectedIds: next };
    });
  }, []);

  const selectAll = useCallback((ids: Id<'knowledgeBaseDocuments'>[]) => {
    setState(prev => {
      const allSelected = ids.every(id => prev.selectedIds.has(id));
      return {
        ...prev,
        selectedIds: allSelected ? new Set() : new Set(ids),
      };
    });
  }, []);

  const resetForm = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  return {
    title: state.title,
    content: state.content,
    isShared: state.isShared,
    isSaving: state.isSaving,
    selectedIds: state.selectedIds,
    setTitle,
    setContent,
    setIsShared,
    setIsSaving,
    toggleSelect,
    selectAll,
    resetForm,
  };
}
